import { Dialog, TextField, Button, Box, FormControlLabel, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import HandoutVisibilitySwitch, { handoutVisibilityToggleSx } from '../../../UIControls/HandoutVisibilitySwitch';
import React, { useContext, useState, useEffect } from 'react';
import { isDuplicateClinicPatientId } from '../../../helper/patientUtils';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import { designTokens } from '../../../../theme';

const AddPatientDialog = ({ open, onClose, onSubmit, initialValues, showSnackbar }) => {
    const { authStore } = useContext(StoreContext);
    const [patientName, setPatientName] = useState('');
    const [patientSurname, setPatientSurname] = useState('');
    const [patientEmail, setPatientEmail] = useState('');
    const [clinicPatientId, setClinicPatientId] = useState('');
    const [patientStatus, setPatientStatus] = useState('active'); // toggleable
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (initialValues && open) {
            setPatientName(initialValues.patientName || '');
            setPatientSurname(initialValues.patientSurname || '');
            setPatientEmail(initialValues.patientEmail || '');
            setClinicPatientId(initialValues.clinicPatientId || '');
            setPatientStatus(
                initialValues.patientStatus === 'archive' ? 'archived' : initialValues.patientStatus || 'active',
            );
        } else if (!initialValues && open) {
            setPatientName('');
            setPatientSurname('');
            setPatientEmail('');
            setClinicPatientId('');
            setPatientStatus('active');
        }
    }, [initialValues, open]);

    const handleSave = async () => {
        const name = patientName.trim();
        const surname = patientSurname.trim();
        const email = patientEmail.trim();

        if (!name) {
            showSnackbar?.('Name is required.', 'error');
            return;
        }

        if (!surname) {
            showSnackbar?.('Surname is required.', 'error');
            return;
        }

        if (email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                showSnackbar?.('Please provide a valid email address.', 'error');
                return;
            }
        }

        const sanitizeField = (value) => {
            return value?.trim() ? value : null;
        };

        const rawData = {
            patientName: name,
            patientSurname: surname,
            patientEmail: sanitizeField(patientEmail),
            clinicPatientId: sanitizeField(clinicPatientId),
            patientStatus,
        };

        // Remove keys with null values
        const cleanedData = Object.fromEntries(Object.entries(rawData).filter(([_, v]) => v !== null));

        setSaving(true);
        try {
            if (clinicPatientId?.trim()) {
                const isDuplicate = await isDuplicateClinicPatientId({
                    bearerToken: authStore.idToken,
                    clinicPatientId: sanitizeField(clinicPatientId),
                    currentPatientId: initialValues?.patientId || null,
                });

                if (isDuplicate) {
                    showSnackbar?.(
                        'Provided PMS ID exists already. Please provide different PMS ID or choose existing patient.',
                        'error',
                    );
                    return;
                }
            }

            await onSubmit(cleanedData);
            onClose();
        } catch (err) {
            console.error('Failed to save patient:', err);
            showSnackbar?.('Failed to save patient. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const inputFieldSx = {
        '& .MuiOutlinedInput-root': {
            height: { xs: 'auto', sm: 52 },
            minHeight: { xs: '36px', sm: 'auto' },
            borderRadius: { xs: '8px', sm: '14px' },
            backgroundColor: '#FFFFFF',
            '& fieldset': { borderColor: '#E5E5E5', borderWidth: { xs: '0.6px', sm: '1.5px' } },
            '&:hover fieldset': { borderColor: '#E5E5E5' },
            '&.Mui-focused fieldset': { borderColor: '#FF669B' },
        },
    };

    const inputFieldPropsSx = {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: '14px', sm: '16px' },
        letterSpacing: { xs: '-0.56px', sm: '-0.64px' },
        color: '#616063',
        borderRadius: { xs: '8px', sm: '14px' },
        height: { xs: 'auto', sm: '52px' },
        minHeight: { xs: '36px', sm: 'auto' },
        alignItems: 'center',
    };

    const saveButtonSx = {
        backgroundColor: '#FF669B',
        color: '#FFFFFF',
        borderRadius: { xs: '100px', sm: 36 },
        width: { xs: '100%', sm: '160px' },
        height: { xs: '35px', sm: '44px' },
        fontSize: { xs: 14, sm: 16 },
        fontWeight: 500,
        lineHeight: { xs: '14px', sm: '16px' },
        letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
        textTransform: 'none',
        '&:hover': { backgroundColor: '#e65588' },
        '&.Mui-disabled': { backgroundColor: 'rgba(255, 102, 155, 0.5)', color: '#FFFFFF' },
    };

    return (
        <>
            <BlockingSpinner open={saving} />
            <Dialog
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        borderRadius: '30px !important',
                        padding: { xs: '30px 20px', sm: '30px 28px' },
                        width: { xs: 'calc(100% - 32px)', sm: '711px' },
                        maxWidth: { xs: 'calc(100% - 32px)', sm: '711px' },
                        maxHeight: 'calc(100dvh - 32px)',
                        boxSizing: 'border-box',
                        overflowY: 'auto',
                    },
                }}
                maxWidth={false}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: { xs: '8px', sm: '24px' },
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: { xs: 26, sm: 32 },
                                lineHeight: { xs: '26px', sm: '32px' },
                                letterSpacing: { xs: '-1.04px', sm: '-1.28px' },
                                color: designTokens.colors.textPrimary,
                                pr: '44px',
                            }}
                        >
                            {initialValues ? 'Edit Patient' : 'Add New Patient'}
                        </Typography>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                p: 0,
                                width: { xs: '28px', sm: '36px' },
                                height: { xs: '28px', sm: '36px' },
                                background: 'rgb(246, 246, 246)',
                                borderRadius: { xs: '11.928px', sm: '50%' },
                                flexShrink: 0,
                            }}
                        >
                            <CloseIcon sx={{ width: { xs: '16px', sm: '24px' }, height: { xs: '16px', sm: '24px' } }} />
                        </IconButton>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Name"
                            value={patientName}
                            onChange={(e) => setPatientName(e.target.value)}
                            required
                            fullWidth
                            sx={inputFieldSx}
                            InputProps={{ sx: inputFieldPropsSx }}
                        />
                        <TextField
                            label="Surname"
                            value={patientSurname}
                            onChange={(e) => setPatientSurname(e.target.value)}
                            required
                            fullWidth
                            sx={inputFieldSx}
                            InputProps={{ sx: inputFieldPropsSx }}
                        />
                        <TextField
                            label="Email"
                            // InputLabelProps={{ shrink: true }}
                            value={patientEmail}
                            onChange={(e) => setPatientEmail(e.target.value)}
                            fullWidth
                            sx={inputFieldSx}
                            InputProps={{ sx: inputFieldPropsSx }}
                        />
                        <TextField
                            label="Clinic Patient ID"
                            value={clinicPatientId}
                            onChange={(e) => setClinicPatientId(e.target.value)}
                            fullWidth
                            sx={inputFieldSx}
                            InputProps={{ sx: inputFieldPropsSx }}
                        />

                        <FormControlLabel
                            control={
                                <HandoutVisibilitySwitch
                                    checked={patientStatus === 'active'}
                                    onChange={(e) => setPatientStatus(e.target.checked ? 'active' : 'archived')}
                                />
                            }
                            label={`Status: ${patientStatus === 'active' ? 'Active' : 'Archived'}`}
                            sx={handoutVisibilityToggleSx}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: '24px', sm: '28px' } }}>
                        <Button onClick={handleSave} disabled={saving} sx={saveButtonSx}>
                            {initialValues ? 'Save Changes' : 'Add'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default AddPatientDialog;
